import { useEffect, useRef, useState } from "react";

type RegistrationConfirmationModalProps = {
  isOpen: boolean;
  title: string;
  intro: string;
  body?: string;
  footer?: string;
  buttonLabel: string;
  onPrimaryAction: () => void;
};

const RegistrationConfirmationModal = ({
  isOpen,
  title,
  intro,
  body,
  footer,
  buttonLabel,
  onPrimaryAction,
}: RegistrationConfirmationModalProps) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setVisible(false);
      return;
    }

    const timer = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusableSelector =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const focusableElements = dialogRef.current?.querySelectorAll<HTMLElement>(
      focusableSelector
    );
    focusableElements?.[0]?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !focusableElements || focusableElements.length === 0) {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement as HTMLElement;

      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        opacity: visible ? 1 : 0,
        transition: "opacity 0.2s ease-in-out",
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          width: "calc(100% - 32px)",
          maxWidth: "480px",
          padding: "24px",
          boxShadow: "0 20px 50px rgba(0, 0, 0, 0.2)",
        }}
      >
        

        <h2 style={{ display: "flex", alignItems: "center", gap: "12px", margin: 0, marginBottom: "10px", fontSize: "24px", fontWeight: 700, color: "#0f172a" }}>
          <div
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            backgroundColor: "#ecfdf3",
            color: "#16a34a",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            marginBottom: "12px",
          }}
        >
          ✓
        </div>{title}
        </h2>
        <p style={{ margin: 0, marginBottom: "10px", color: "#334155", lineHeight: 1.6 }}>{intro}</p>
        {body ? (
          <p style={{ margin: 0, marginBottom: "10px", color: "#334155", lineHeight: 1.6 }}>{body}</p>
        ) : null}
        {footer ? (
          <p style={{ margin: 0, marginBottom: "20px", color: "#334155", lineHeight: 1.6 }}>{footer}</p>
        ) : null}

        <button
          type="button"
          onClick={onPrimaryAction}
          style={{
            width: "100%",
            border: "none",
            borderRadius: "10px",
            padding: "12px 16px",
            backgroundColor: "#4285F4",
            color: "#fff",
            fontSize: "15px",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {buttonLabel}
        </button>
      </div>
    </div>
  );
};

export default RegistrationConfirmationModal;
